import React from "react";
import { Box, Typography, IconButton, Tooltip, useTheme } from "@mui/material";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ArchiveIcon from "@mui/icons-material/Archive";
import UnarchiveIcon from "@mui/icons-material/Unarchive";
import { useNavigate } from "react-router-dom";
import ReusableTable from "../../../../components/ReusableTable/ReusableTable";
import VendorRiskBadge from "../../components/VendorRiskBadge";
import StatusChip from "../../components/StatusChip";
import { VENDOR_SOURCES, getDisplayedReviewStatus } from "../../utils/constants";
import { getOwnerLabel } from "../../hooks/useVendorStaff";

const formatDate = (iso) => {
  if (!iso) return "-";
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const columns = [
  { label: "Vendor", field: "name", align: "left", width: "16%", minWidth: 150 },
  { label: "Category", field: "category", align: "left", width: "16%", minWidth: 150 },
  { label: "Owner", field: "owner", align: "left", width: "14%", minWidth: 130 },
  { label: "Inherent Risk", field: "inherentRisk", align: "left", width: "12%", minWidth: 110 },
  { label: "Source", field: "source", align: "left", width: "8%", minWidth: 90 },
  { label: "Review Status", field: "reviewState", align: "left", width: "14%", minWidth: 130 },
  { label: "Next Review", field: "nextReviewDate", align: "left", width: "12%", minWidth: 120 },
  { label: "Actions", field: "actions", align: "center", width: "8%", minWidth: 88 },
];

const VendorTable = ({ vendors = [], onArchive, people = [] }) => {
  const theme = useTheme();
  const navigate = useNavigate();

  const renderCell = (row, column) => {
    const archivedStyle =
      row.status === "Archived"
        ? { opacity: 0.6, color: theme.customColors.text.secondary }
        : {};

    switch (column.field) {
      case "name":
        return (
          <Typography
            variant="body2"
            sx={{
              ...archivedStyle,
              color: theme.customColors.text.strong,
              fontWeight: 500,
              whiteSpace: "normal",
              wordBreak: "break-word",
            }}
          >
            {row.name}
          </Typography>
        );
      case "owner":
        return (
          <Typography variant="body2" sx={{ ...archivedStyle, whiteSpace: "normal" }}>
            {getOwnerLabel(people, row.owner)}
          </Typography>
        );
      case "category":
        return (
          <Typography
            variant="body2"
            sx={{ ...archivedStyle, color: theme.customColors.text.strong, whiteSpace: "normal" }}
          >
            {row.category || "-"}
          </Typography>
        );
      case "inherentRisk":
        return <VendorRiskBadge level={row.inherentRisk} />;
      case "source":
        return (
          <Typography variant="body2" sx={archivedStyle}>
            {row.source === VENDOR_SOURCES.DISCOVERED ? "Discovered" : "Manual"}
          </Typography>
        );
      case "reviewState":
        return <StatusChip label={getDisplayedReviewStatus(row)} />;
      case "nextReviewDate":
        return (
          <Typography variant="body2" sx={archivedStyle}>
            {formatDate(row.nextReviewDate)}
          </Typography>
        );
      case "actions":
        return (
          <Box sx={{ display: "flex", justifyContent: "center", gap: 0.5 }}>
            <Tooltip title={row.status === "Archived" ? "Restore" : "Archive"} arrow>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onArchive(row.id);
                }}
                sx={{ color: theme.customColors.icons.dark }}
              >
                {row.status === "Archived" ? (
                  <UnarchiveIcon sx={{ fontSize: 16 }} />
                ) : (
                  <ArchiveIcon sx={{ fontSize: 16 }} />
                )}
              </IconButton>
            </Tooltip>
            <Tooltip title="View Details" arrow>
              <IconButton
                size="small"
                onClick={() => navigate(`/admin/vendors/${row.id}`)}
                sx={{
                  color: theme.customColors.icons.dark,
                  "&:hover": {
                    backgroundColor: theme.customColors.background.light,
                    transform: "translateX(2px)",
                    transition: "all 0.2s ease",
                  },
                }}
              >
                <ArrowForwardIosIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          </Box>
        );
      default:
        return null;
    }
  };

  return (
    <ReusableTable
      columns={columns}
      data={vendors}
      renderCell={renderCell}
      getRowProps={(row) => ({
        onClick: () => navigate(`/admin/vendors/${row.id}`),
        sx: { cursor: "pointer" },
      })}
      getCellSx={() => ({
        overflow: "visible",
        textOverflow: "unset",
        whiteSpace: "normal",
      })}
      rowsPerPageOptions={[10, 20, 50, 100]}
      height="auto"
      showPagination
    />
  );
};

export default VendorTable;
